import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const empty = { name: "", email: "", phone: "", address: "", service_description: "", default_amount: "", tax_rate: "" };

export const ClientFormDialog = ({ open, onOpenChange, client, defaultTax, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(client ? { ...client, tax_rate: client.tax_rate ?? "" } : empty);
  }, [open, client]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      default_amount: Number(form.default_amount) || 0,
      tax_rate: form.tax_rate === "" ? null : Number(form.tax_rate),
      service_description: form.service_description || "Service",
    };
    try {
      const r = client ? await api.put(`/clients/${client.id}`, payload) : await api.post("/clients", payload);
      toast.success(client ? "Client updated" : "Client added");
      onSaved(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{client ? "Edit Client" : "New Client"}</DialogTitle><DialogDescription>Saved once, invoiced in one tap.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Client name *</Label>
            <Input data-testid="client-form-name-input" required value={form.name} onChange={set("name")} placeholder="Johnson Residence" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Email (for sending invoices)</Label>
              <Input data-testid="client-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="client@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="client-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Service address</Label>
            <Input data-testid="client-form-address-input" value={form.address} onChange={set("address")} placeholder="123 Main St, Austin, TX" />
          </div>
          <div className="space-y-1.5">
            <Label>Default service description</Label>
            <Input data-testid="client-form-service-input" value={form.service_description} onChange={set("service_description")} placeholder="Weekly lawn mowing & edging" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Default amount ($)</Label>
              <Input data-testid="client-form-amount-input" type="number" step="0.01" min="0" required value={form.default_amount} onChange={set("default_amount")} placeholder="65.00" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Tax rate % <span className="text-muted-foreground font-normal">(default {defaultTax}%)</span></Label>
              <Input data-testid="client-form-tax-rate-input" type="number" step="0.001" min="0" max="100" value={form.tax_rate} onChange={set("tax_rate")} placeholder={`${defaultTax}`} className="font-mono" />
            </div>
          </div>
          <Button data-testid="client-form-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {busy ? "Saving..." : client ? "Save Changes" : "Add Client"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
